import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, setDoc, doc, getDoc, onSnapshot } from 'firebase/firestore';

// --- CONSTANTS & HELPERS (Moved outside component) ---
const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const getOrdinalDate = (date) => {
    const d = date.getDate();
    const m = monthNames[date.getMonth()];
    const suffix = (d % 10 === 1 && d !== 11) ? "st" : (d % 10 === 2 && d !== 12) ? "nd" : (d % 10 === 3 && d !== 13) ? "rd" : "th";
    return `${d}${suffix} ${m}`;
};

const getFormattedDateKey = (date) => {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

// --- DEFAULT CALCULATORS (Fallback) ---
const calculateDefaultCutoff = (year, month) => {
    const payday23 = new Date(year, month, 23);
    const dayOfWeek = payday23.getDay(); 
    let cutoffDay;
    if (dayOfWeek === 6) cutoffDay = 20; 
    else if (dayOfWeek === 0) cutoffDay = 19; 
    else if (dayOfWeek === 1) cutoffDay = 19; 
    else cutoffDay = 21; 
    return new Date(year, month, cutoffDay);
};

const calculateDefaultProcessDate = (year, month) => {
    const payday23 = new Date(year, month, 23);
    const dayOfWeek = payday23.getDay(); 
    let processDay;
    if (dayOfWeek === 6) processDay = 21; 
    else if (dayOfWeek === 0) processDay = 20; 
    else if (dayOfWeek === 1) processDay = 20; 
    else processDay = 22; 
    return new Date(year, month, processDay);
};

const calculateDefaultPayday = (year, month) => {
    const payday23 = new Date(year, month, 23);
    const dayOfWeek = payday23.getDay(); 
    if (dayOfWeek === 6) payday23.setDate(22); 
    else if (dayOfWeek === 0) payday23.setDate(21); 
    return payday23;
};

// --- COMPONENT ---
const Timesheet = ({ db, initials }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [timesheetData, setTimesheetData] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [userDetails, setUserDetails] = useState(null); 
    
    // State for dynamic date range (Raw data from DB)
    const [currentPeriodConfig, setCurrentPeriodConfig] = useState(null);
    const [prevPeriodConfig, setPrevPeriodConfig] = useState(null);

    // Derived Pay Period State
    const [payPeriod, setPayPeriod] = useState({ 
        startDate: new Date(), 
        endDate: new Date(), 
        message: '' 
    });
    
    const [startTime, setStartTime] = useState('');
    const [finishTime, setFinishTime] = useState('');
    const [isOvernight, setIsOvernight] = useState(false);

    // --- 1. LISTEN TO PAY PERIOD CONFIGURATION (Real-time) ---
    useEffect(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // Listen for Current Month Config
        const docId = `period_${year}_${month}`;
        const unsubCurrent = onSnapshot(doc(db, "pay_periods", docId), (docSnap) => {
            if (docSnap.exists()) {
                setCurrentPeriodConfig(docSnap.data());
            } else {
                setCurrentPeriodConfig(null); // Fallback to defaults
            }
        });

        // Listen for Previous Month Config (to get start date)
        const prevDate = new Date(year, month - 1, 1);
        const prevDocId = `period_${prevDate.getFullYear()}_${prevDate.getMonth()}`;
        const unsubPrev = onSnapshot(doc(db, "pay_periods", prevDocId), (docSnap) => {
            if (docSnap.exists()) {
                setPrevPeriodConfig(docSnap.data());
            } else {
                setPrevPeriodConfig(null);
            }
        });

        return () => {
            unsubCurrent();
            unsubPrev();
        };
    }, [db, currentDate]);

    // --- 2. CALCULATE PAY PERIOD (Runs when config or date changes) ---
    useEffect(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // End Date Logic
        let cutoffDateEnd, processDate, paydayDate;
        
        if (currentPeriodConfig) {
            cutoffDateEnd = new Date(currentPeriodConfig.cutoffDate);
            processDate = new Date(currentPeriodConfig.processDate);
            paydayDate = new Date(currentPeriodConfig.payday);
        } else {
            cutoffDateEnd = calculateDefaultCutoff(year, month);
            processDate = calculateDefaultProcessDate(year, month);
            paydayDate = calculateDefaultPayday(year, month);
        }

        // Start Date Logic
        let prevCutoff;
        if (prevPeriodConfig) {
            prevCutoff = new Date(prevPeriodConfig.cutoffDate);
        } else {
            const prevDate = new Date(year, month - 1, 1);
            prevCutoff = calculateDefaultCutoff(prevDate.getFullYear(), prevDate.getMonth());
        }

        const startDate = new Date(prevCutoff);
        startDate.setDate(startDate.getDate() + 1); // Start date is 1 day after prev cutoff

        setPayPeriod({
            startDate,
            endDate: cutoffDateEnd,
            message: `Payday: ${getOrdinalDate(paydayDate)}. Shifts up to ${getOrdinalDate(cutoffDateEnd)} must be submitted by 9am ${getOrdinalDate(processDate)}.`
        });

    }, [currentPeriodConfig, prevPeriodConfig, currentDate]);

    // --- FETCH USER & TIMESHEETS ---
    useEffect(() => {
        const fetchUserData = async () => {
            if (!initials) return;
            const docRef = doc(db, "users", initials);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setUserDetails(docSnap.data());
            }
        };
        fetchUserData();
    }, [db, initials]);

    const fetchTimesheets = async () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const prevDate = new Date(year, month - 1, 1);
        
        const q1 = query(collection(db, "timesheets"), where("initials", "==", initials), where("year", "==", year), where("month", "==", month));
        const s1 = await getDocs(q1);

        const q2 = query(collection(db, "timesheets"), where("initials", "==", initials), where("year", "==", prevDate.getFullYear()), where("month", "==", prevDate.getMonth()));
        const s2 = await getDocs(q2);

        const data = {};
        const process = (doc) => {
            const r = doc.data();
            const key = `${r.year}-${r.month}-${r.day}`;
            data[key] = { ...r };
        };
        s1.forEach(process);
        s2.forEach(process);
        setTimesheetData(data);
    };

    useEffect(() => {
        fetchTimesheets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDate, initials]);

    // --- EVENT HANDLERS ---
    const handleDayClick = (day, month, year) => {
        const key = `${year}-${month}-${day}`;
        const data = timesheetData[key] || {};
        
        setSelectedDate({ day, month, year });
        setStartTime(data.start || '');
        setFinishTime(data.finish || '');
        setIsOvernight(data.isOvernight || false);
        setIsPopupOpen(true);
    };

    const handleSave = async () => {
        if (!selectedDate) return;
        const docId = `${initials}_${selectedDate.year}_${selectedDate.month}_${selectedDate.day}_timesheet`;
        
        await setDoc(doc(db, "timesheets", docId), {
            initials,
            year: selectedDate.year,
            month: selectedDate.month,
            day: selectedDate.day,
            start: startTime,
            finish: finishTime,
            isOvernight: isOvernight,
            updatedAt: new Date()
        }, { merge: true });

        setIsPopupOpen(false);
        fetchTimesheets(); 
    };

    const changeMonth = (offset) => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1));
    };

    // --- HELPER FUNCS ---
    const snapToQuarter = (timeStr, setFunc) => {
        if (!timeStr) return;
        const [h, m] = timeStr.split(':').map(Number);
        let roundedM = Math.round(m / 15) * 15;
        let newH = h;
        if (roundedM === 60) { roundedM = 0; newH = (newH + 1) % 24; }
        setFunc(`${String(newH).padStart(2, '0')}:${String(roundedM).padStart(2, '0')}`);
    };

    const calculateUnsocialHoursForShift = (startStr, finishStr) => {
        if (!startStr || !finishStr) return 0;
        const s = new Date(`1970-01-01T${startStr}`);
        const f = new Date(`1970-01-01T${finishStr}`);
        if (f < s) f.setDate(f.getDate() + 1); 

        const d1_00 = new Date(s); d1_00.setHours(0,0,0,0);
        const d1_06 = new Date(s); d1_06.setHours(6,0,0,0);
        const d1_20 = new Date(s); d1_20.setHours(20,0,0,0);
        const d1_24 = new Date(s); d1_24.setHours(24,0,0,0);
        const d2_00 = new Date(s); d2_00.setDate(d2_00.getDate() + 1); d2_00.setHours(0,0,0,0);
        const d2_06 = new Date(s); d2_06.setDate(d2_06.getDate() + 1); d2_06.setHours(6,0,0,0);

        const getOverlap = (start, end, wStart, wEnd) => {
            const maxStart = new Date(Math.max(start, wStart));
            const minEnd = new Date(Math.min(end, wEnd));
            return Math.max(0, (minEnd - maxStart) / (1000 * 60 * 60)); 
        };

        let unsoc = 0;
        unsoc += getOverlap(s, f, d1_00, d1_06);
        unsoc += getOverlap(s, f, d1_20, d1_24);
        unsoc += getOverlap(s, f, d2_00, d2_06);
        return unsoc;
    };

    // Helper: Check if a specific date is within the current calculated pay period
    const isDateInPayPeriod = (d, m, y) => {
        const checkDate = new Date(y, m, d);
        const { startDate, endDate } = payPeriod;
        checkDate.setHours(0,0,0,0);
        const s = new Date(startDate); s.setHours(0,0,0,0);
        const e = new Date(endDate); e.setHours(0,0,0,0);
        return checkDate >= s && checkDate <= e;
    };

    // --- CALCULATE TOTALS ---
    const calculateTotals = () => {
        const { startDate, endDate } = payPeriod;
        let totals = { weekdayOvertime: 0, weekendOvertime: 0, unsoc: 0, travel: 0, overnight: 0 };

        // Iterate through dates in range
        let loopDate = new Date(startDate);
        while (loopDate <= endDate) {
            const key = getFormattedDateKey(loopDate);
            const data = timesheetData[key];

            if (data && data.start && data.finish) {
                const s = new Date(`1970-01-01T${data.start}`);
                const f = new Date(`1970-01-01T${data.finish}`);
                let diffMs = f - s;
                if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
                const durationHrs = diffMs / (1000 * 60 * 60);

                totals.unsoc += calculateUnsocialHoursForShift(data.start, data.finish);
                if (userDetails?.travelAllowance === 'Yes') totals.travel++;
                if (data.isOvernight) totals.overnight++;

                const dayOfWeek = loopDate.getDay(); 
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                if (isWeekend) {
                    totals.weekendOvertime += durationHrs;
                } else {
                    if (userDetails?.salaried !== 'Yes' && userDetails?.contractedHours) {
                        const contracted = parseFloat(userDetails.contractedHours);
                        const lunchMins = parseFloat(userDetails.defaultLunch) || 0;
                        const lunchHours = lunchMins / 60;
                        const netWorked = durationHrs - lunchHours;
                        const dailyOvertime = netWorked - contracted;
                        if (dailyOvertime > 0) totals.weekdayOvertime += dailyOvertime;
                    }
                }
            } else if (data && data.isOvernight) {
                 totals.overnight++;
            }
            loopDate.setDate(loopDate.getDate() + 1);
        }
        return totals;
    };

    const totals = calculateTotals();
    const formatDateShort = (d) => `${d.getDate()} ${monthNames[d.getMonth()].substring(0,3)}`;

    // --- RENDER GRID ---
    const renderCalendarGrid = () => {
        const { startDate, endDate } = payPeriod;
        const days = [];
        
        // Calculate offset for Monday start
        let firstDayIndex = startDate.getDay();
        firstDayIndex = firstDayIndex === 0 ? 6 : firstDayIndex - 1; 
        
        for (let i = 0; i < firstDayIndex; i++) {
            days.push(<div key={`b-${i}`} className="calendar-day empty"></div>);
        }

        let loopDate = new Date(startDate);
        while (loopDate <= endDate) {
            const d = loopDate.getDate();
            const m = loopDate.getMonth();
            const y = loopDate.getFullYear();
            const key = getFormattedDateKey(loopDate);
            const data = timesheetData[key];
            const hasEntry = data && (data.start || data.finish);
            
            // VISUAL: Dim if outside pay period (Should be minimal now as we only render the period)
            const isActivePeriod = isDateInPayPeriod(d, m, y);
            const opacity = isActivePeriod ? 1 : 0.4;
            const bgColor = hasEntry ? 'rgba(94,106,210,0.28)' : (isActivePeriod ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)');
            
            // Capture values for closure
            const currentD = d, currentM = m, currentY = y; 

            days.push(
                <div 
                    key={key} 
                    className="calendar-day" 
                    onClick={() => handleDayClick(currentD, currentM, currentY)}
                    style={{
                        backgroundColor: bgColor, 
                        opacity: opacity,
                        color: hasEntry ? '#c7d2fe' : 'var(--text-main)',
                        fontWeight: hasEntry ? 'bold' : 'normal',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '50px',
                        fontSize: '12px',
                        border: data?.isOvernight ? '2px solid #9333ea' : '1px solid var(--border)'
                    }}
                >
                    <span style={{marginBottom: hasEntry ? '2px' : '0'}}>{d} <span style={{fontSize:'9px', color:'#94a3b8'}}>{monthNames[m].substring(0,3)}</span></span>
                    {hasEntry && <span style={{fontSize:'9px', lineHeight:'1.1'}}>{data.start}<br/>{data.finish}</span>}
                </div>
            );
            loopDate.setDate(loopDate.getDate() + 1);
        }
        return days;
    };

    return (
        <div className="animate-fade-in">
            {/* Pay Period Info */}
            <div className="card" style={{marginBottom:'15px', backgroundColor:'rgba(94,106,210,0.12)', border:'1px solid rgba(94,106,210,0.35)', padding:'12px', textAlign:'center'}}>
                <p style={{margin:0, color:'#a5b0f0', fontWeight:'bold', fontSize:'14px'}}>
                    Pay Period: {formatDateShort(payPeriod.startDate)} - {formatDateShort(payPeriod.endDate)}
                </p>
                <p style={{margin:'4px 0 0 0', fontSize:'11px', color:'#0284c7'}}>
                    {payPeriod.message}
                </p>
            </div>

            {/* Contract Info */}
            {userDetails && (
                <div className="card" style={{marginBottom:'15px', backgroundColor:'#f0f9ff', border:'1px solid #bae6fd', padding:'15px'}}>
                   <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', fontSize:'12px', color:'#0c4a6e'}}>
                       <div><strong>Contracted Hrs:</strong> {userDetails.contractedHours || '-'} /day</div>
                       <div><strong>Lunch:</strong> {userDetails.defaultLunch || '-'} mins</div>
                       <div><strong>Fuel Allowance:</strong> {userDetails.travelAllowance || 'No'}</div>
                       <div><strong>Salaried:</strong> {userDetails.salaried || 'No'}</div>
                   </div>
                </div>
            )}

            {/* Navigation */}
            <div className="card" style={{marginBottom:'15px', padding:'10px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <button onClick={() => changeMonth(-1)} style={{background:'transparent', color:'var(--text-main)', padding:'5px'}}>←</button>
                <h4 style={{fontSize:'14px', textTransform:'uppercase', color:'var(--text-muted)', margin:0}}>
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h4>
                <button onClick={() => changeMonth(1)} style={{background:'transparent', color:'var(--text-main)', padding:'5px'}}>→</button>
            </div>

            {/* Calendar Grid */}
            <div className="card" style={{padding:'5px'}}>
                <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'2px', textAlign:'center', fontSize:'10px', fontWeight:'bold', marginBottom:'5px'}}>
                    <div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div><div>Sun</div>
                </div>
                <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'2px'}}>
                    {renderCalendarGrid()}
                </div>
            </div>

            {/* Summaries */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:'10px', marginTop:'15px'}}>
                <div className="card" style={{padding:'15px', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(27,143,90,0.12)', border:'1px solid #86efac'}}>
                    <div style={{fontSize:'11px', textTransform:'uppercase', color:'#15803d', fontWeight:'bold', marginBottom:'5px'}}>Weekly Overtime</div>
                    <div style={{fontSize:'20px', fontWeight:'800', color:'#166534', lineHeight:'1'}}>
                        {totals.weekdayOvertime === null ? 'N/A' : <>{totals.weekdayOvertime.toFixed(2)} <span style={{fontSize:'12px', fontWeight:'normal', color:'#15803d'}}>hrs</span></>}
                    </div>
                </div>
                <div className="card" style={{padding:'15px', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(217,119,6,0.12)', border:'1px solid #fdba74'}}>
                    <div style={{fontSize:'11px', textTransform:'uppercase', color:'#9a3412', fontWeight:'bold', marginBottom:'5px'}}>Weekend Overtime</div>
                    <div style={{fontSize:'20px', fontWeight:'800', color:'#c2410c', lineHeight:'1'}}>{totals.weekendOvertime.toFixed(2)} <span style={{fontSize:'12px', fontWeight:'normal', color:'#9a3412'}}>hrs</span></div>
                </div>
                <div className="card" style={{padding:'15px', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(124,77,255,0.12)', border:'1px solid rgba(124,77,255,0.35)'}}>
                    <div style={{fontSize:'11px', textTransform:'uppercase', color:'#6b21a8', fontWeight:'bold', marginBottom:'5px'}}>Unsociable</div>
                    <div style={{fontSize:'20px', fontWeight:'800', color:'#581c87', lineHeight:'1'}}>{totals.unsoc.toFixed(2)} <span style={{fontSize:'12px', fontWeight:'normal', color:'#6b21a8'}}>hrs</span></div>
                </div>
                <div className="card" style={{padding:'15px', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(34,211,238,0.1)', border:'1px solid rgba(34,211,238,0.35)'}}>
                    <div style={{fontSize:'11px', textTransform:'uppercase', color:'#0e7490', fontWeight:'bold', marginBottom:'5px'}}>Fuel Allowance</div>
                    <div style={{fontSize:'20px', fontWeight:'800', color:'#155e75', lineHeight:'1'}}>
                        {userDetails?.travelAllowance === 'Yes' ? <>{totals.travel} <span style={{fontSize:'12px', fontWeight:'normal', color:'#0e7490'}}>shifts</span></> : 'N/A'}
                    </div>
                </div>
                <div className="card" style={{padding:'15px', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(168,85,247,0.1)', border:'1px solid rgba(168,85,247,0.35)'}}>
                    <div style={{fontSize:'11px', textTransform:'uppercase', color:'#7e22ce', fontWeight:'bold', marginBottom:'5px'}}>Overnights</div>
                    <div style={{fontSize:'20px', fontWeight:'800', color:'#6b21a8', lineHeight:'1'}}>{totals.overnight} <span style={{fontSize:'12px', fontWeight:'normal', color:'#7e22ce'}}>nights</span></div>
                </div>
            </div>

            {isPopupOpen && selectedDate && (
                <div className="popup-overlay">
                    <div className="card animate-fade-in" style={{width:'90%', maxWidth:'350px', padding:'20px'}}>
                        <div className="flex-between" style={{marginBottom:'20px', borderBottom:'1px solid var(--border)', paddingBottom:'10px'}}>
                            <h3 style={{margin:0}}>{selectedDate.day} {monthNames[selectedDate.month]}</h3>
                            <button onClick={()=>setIsPopupOpen(false)} style={{background:'transparent', fontSize:'18px', color:'var(--text-main)', padding:0}}>✕</button>
                        </div>
                        <div style={{display:'grid', gap:'15px', marginBottom:'20px'}}>
                            <div><label style={{display:'block', fontSize:'12px', fontWeight:'bold', marginBottom:'5px'}}>Start Time</label><input type="time" step="900" value={startTime} onChange={e => setStartTime(e.target.value)} onBlur={e => snapToQuarter(e.target.value, setStartTime)} style={{width:'100%', padding:'10px', fontSize:'16px'}}/></div>
                            <div><label style={{display:'block', fontSize:'12px', fontWeight:'bold', marginBottom:'5px'}}>Finish Time</label><input type="time" step="900" value={finishTime} onChange={e => setFinishTime(e.target.value)} onBlur={e => snapToQuarter(e.target.value, setFinishTime)} style={{width:'100%', padding:'10px', fontSize:'16px'}}/></div>
                            <div style={{display:'flex', alignItems:'center', gap:'8px', marginTop:'5px'}}><input type="checkbox" checked={isOvernight} onChange={e => setIsOvernight(e.target.checked)} style={{width:'18px', height:'18px'}}/><label style={{fontSize:'14px', fontWeight:'bold'}}>Overnight?</label></div>
                        </div>
                        <button onClick={handleSave} style={{width:'100%', background:'var(--primary)', color:'white', padding:'12px', fontSize:'16px'}}>Save Times</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Timesheet;